// Attorney Portal Types
// BP-10-04: Attorney Frontend

// ============================================================================
// Enums
// ============================================================================

export enum CaseStatus {
  INTAKE = 'intake',
  INVESTIGATION = 'investigation',
  FILED = 'filed',
  DISCOVERY = 'discovery',
  MEDIATION = 'mediation',
  TRIAL = 'trial',
  SETTLEMENT = 'settlement',
  CLOSED = 'closed',
}

export enum CaseEventType {
  FILING = 'filing',
  HEARING = 'hearing',
  DEPOSITION = 'deposition',
  MEDIATION = 'mediation',
  MOTION = 'motion',
  DOCUMENT = 'document',
  NOTE = 'note',
  MILESTONE = 'milestone',
}

export enum EventPriority {
  HIGH = 'high',
  MEDIUM = 'medium',
  LOW = 'low',
}

export enum LifeCarePlanStatus {
  DRAFT = 'draft',
  REVIEW = 'review',
  FINAL = 'final',
}

// ============================================================================
// Attorney Profile
// ============================================================================

export interface AttorneyProfile {
  id: string;
  userId: string;
  firmName: string;
  firmWebsite?: string;
  barNumber: string;
  barState: string;
  additionalBarStates: string[];
  yearsPracticing: number;
  practiceAreas: string[];
  bio?: string;
  education?: string;
  notableCases?: string;
  photoUrl?: string;
  isVerified: boolean;
  verifiedAt?: string;
  verificationDocuments?: Record<string, unknown>;
  referralSource?: string;
  createdAt: string;
}

export interface AttorneyProfileFormData {
  firmName: string;
  firmWebsite?: string;
  barNumber: string;
  barState: string;
  additionalBarStates: string[];
  yearsPracticing: number;
  practiceAreas: string[];
  bio?: string;
  education?: string;
  notableCases?: string;
}

// ============================================================================
// Legal Cases
// ============================================================================

export interface LegalCase {
  id: string;
  caseNumber: string;
  internalReference?: string;
  familyId: string;
  attorneyId: string;
  coCounselIds: string[];
  caseType: string;
  defendant: string;
  defendantCounsel?: string;
  court?: string;
  jurisdiction?: string;
  judge?: string;
  incidentDate?: string;
  filedDate?: string;
  statuteOfLimitations?: string;
  status: CaseStatus;
  estimatedValue?: number;
  settlementAmount?: number;
  caseSummary?: string;
  liabilityAssessment?: string;
  internalNotes?: string;
  clientPortalEnabled: boolean;
  settlementStructure?: SettlementStructure;
  createdAt: string;
  updatedAt: string;
  closedAt?: string;
  // Expanded relations
  family?: {
    id: string;
    name: string;
    primaryContactEmail: string;
  };
  eventCount?: number;
  documentCount?: number;
  upcomingEvents?: CaseEvent[];
}

export interface LegalCaseFormData {
  caseNumber: string;
  internalReference?: string;
  familyId?: string;
  caseType: string;
  defendant: string;
  defendantCounsel?: string;
  court?: string;
  jurisdiction?: string;
  judge?: string;
  incidentDate?: string;
  filedDate?: string;
  statuteOfLimitations?: string;
  status: CaseStatus;
  estimatedValue?: number;
  caseSummary?: string;
  liabilityAssessment?: string;
  internalNotes?: string;
  clientPortalEnabled: boolean;
}

export interface CaseFilters {
  status?: CaseStatus[];
  caseType?: string;
  search?: string;
  dateFrom?: string;
  dateTo?: string;
}

export interface CaseSummaryStats {
  totalCases: number;
  activeCases: number;
  settledCases: number;
  totalSettlementValue: number;
  averageSettlement: number;
  casesInDiscovery: number;
  casesInTrial: number;
  upcomingDeadlines: number;
}

// ============================================================================
// Case Events
// ============================================================================

export interface CaseEvent {
  id: string;
  caseId: string;
  createdById: string;
  eventType: CaseEventType;
  title: string;
  description?: string;
  eventDate?: string;
  dueDate?: string;
  completedAt?: string;
  visibleToClient: boolean;
  priority: EventPriority;
  attachments?: EventAttachment[];
  createdAt: string;
  createdBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

export interface EventAttachment {
  id: string;
  filename: string;
  fileUrl: string;
  fileSize: number;
}

export interface CaseEventFormData {
  eventType: CaseEventType;
  title: string;
  description?: string;
  eventDate?: string;
  dueDate?: string;
  visibleToClient: boolean;
  priority: EventPriority;
}

// ============================================================================
// Life Care Plans
// ============================================================================

export interface LifeCarePlan {
  id: string;
  caseId: string;
  childId: string;
  preparerName: string;
  preparerCredentials?: string;
  preparerOrganization?: string;
  preparationDate: string;
  lifeExpectancyYears: number;
  lifeExpectancySource?: string;
  medicalCosts: CostCategory[];
  therapyCosts: CostCategory[];
  equipmentCosts: CostCategory[];
  homeModificationCosts: CostCategory[];
  careCosts: CostCategory[];
  transportationCosts: CostCategory[];
  educationCosts: CostCategory[];
  annualCostCurrent: number;
  annualCostAge18: number;
  totalLifetimeCost: number;
  assumptions?: string;
  methodology?: string;
  documentId?: string;
  status: LifeCarePlanStatus;
  createdAt: string;
  updatedAt: string;
  child?: {
    id: string;
    firstName: string;
    lastName: string;
    dateOfBirth: string;
  };
}

export interface CostCategory {
  id: string;
  category: string;
  item: string;
  annualCost: number;
  lifetimeCost: number;
  startAge?: number;
  endAge?: number;
  frequency?: string;
  notes?: string;
}

export interface LifeCarePlanFormData {
  childId: string;
  preparerName: string;
  preparerCredentials?: string;
  preparerOrganization?: string;
  preparationDate: string;
  lifeExpectancyYears: number;
  lifeExpectancySource?: string;
  assumptions?: string;
  methodology?: string;
  status: LifeCarePlanStatus;
}

export interface CostCategoryFormData {
  category: string;
  item: string;
  annualCost: number;
  startAge?: number;
  endAge?: number;
  frequency?: string;
  notes?: string;
}

// ============================================================================
// Settlement Planning
// ============================================================================

export interface SettlementStructure {
  totalAmount: number;
  immediatePayment: number;
  structuredPayments: StructuredPayment[];
  presentValue: number;
  taxImplications?: string;
  trustDetails?: TrustDetails;
}

export interface StructuredPayment {
  id: string;
  paymentType: 'lump_sum' | 'periodic' | 'life_contingent';
  amount: number;
  startDate?: string;
  endDate?: string;
  frequency?: 'monthly' | 'quarterly' | 'annually';
  growthRate?: number;
  description?: string;
}

export interface TrustDetails {
  trustType: 'special_needs' | 'pooled' | 'first_party' | 'third_party';
  trustName?: string;
  trusteeName?: string;
  fundingAmount: number;
  notes?: string;
}

export interface SettlementCalculation {
  grossSettlement: number;
  attorneyFees: number;
  attorneyFeePercentage: number;
  litigationCosts: number;
  medicalLiens: number;
  otherDeductions: number;
  netToClient: number;
  structuredValue: number;
  presentValueDiscount: number;
}

export interface SettlementScenario {
  id: string;
  name: string;
  calculation: SettlementCalculation;
  structure: SettlementStructure;
  createdAt: string;
}

// ============================================================================
// Shared Documents
// ============================================================================

export interface SharedDocument {
  id: string;
  documentId: string;
  caseId: string;
  sharedAt: string;
  sharedById: string;
  document: {
    id: string;
    title: string;
    originalFilename: string;
    fileType: string;
    fileSizeBytes: number;
    documentType: string;
    documentDate?: string;
    providerName?: string;
  };
  sharedBy?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

// ============================================================================
// Client Portal (Family View)
// ============================================================================

export interface ClientCaseView {
  caseId: string;
  caseNumber: string;
  status: CaseStatus;
  attorneyName: string;
  firmName: string;
  attorneyEmail: string;
  attorneyPhone?: string;
  lastUpdate: string;
  visibleEvents: CaseEvent[];
  sharedDocuments: SharedDocument[];
  messages?: CaseMessage[];
}

export interface CaseMessage {
  id: string;
  caseId: string;
  senderId: string;
  senderType: 'attorney' | 'client';
  content: string;
  isRead: boolean;
  createdAt: string;
  sender?: {
    id: string;
    firstName: string;
    lastName: string;
  };
}

// ============================================================================
// API Response Types
// ============================================================================

export interface PaginatedCases {
  items: LegalCase[];
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

export interface CaseTimelineResponse {
  events: CaseEvent[];
  total: number;
}

export interface AttorneyDashboardStats {
  summary: CaseSummaryStats;
  recentActivity: CaseEvent[];
  upcomingDeadlines: CaseEvent[];
  casesByStatus: Record<CaseStatus, number>;
}

// ============================================================================
// Form Validation Schemas (for use with Zod)
// ============================================================================

export const CASE_STATUS_OPTIONS = [
  { value: CaseStatus.INTAKE, label: 'Intake' },
  { value: CaseStatus.INVESTIGATION, label: 'Investigation' },
  { value: CaseStatus.FILED, label: 'Filed' },
  { value: CaseStatus.DISCOVERY, label: 'Discovery' },
  { value: CaseStatus.MEDIATION, label: 'Mediation' },
  { value: CaseStatus.TRIAL, label: 'Trial' },
  { value: CaseStatus.SETTLEMENT, label: 'Settlement' },
  { value: CaseStatus.CLOSED, label: 'Closed' },
];

export const EVENT_TYPE_OPTIONS = [
  { value: CaseEventType.FILING, label: 'Filing' },
  { value: CaseEventType.HEARING, label: 'Hearing' },
  { value: CaseEventType.DEPOSITION, label: 'Deposition' },
  { value: CaseEventType.MEDIATION, label: 'Mediation' },
  { value: CaseEventType.MOTION, label: 'Motion' },
  { value: CaseEventType.DOCUMENT, label: 'Document' },
  { value: CaseEventType.NOTE, label: 'Note' },
  { value: CaseEventType.MILESTONE, label: 'Milestone' },
];

export const PRIORITY_OPTIONS = [
  { value: EventPriority.HIGH, label: 'High' },
  { value: EventPriority.MEDIUM, label: 'Medium' },
  { value: EventPriority.LOW, label: 'Low' },
];

export const COST_CATEGORIES = [
  'Medical Care',
  'Therapy Services',
  'Durable Medical Equipment',
  'Home Modifications',
  'Attendant Care',
  'Transportation',
  'Education',
  'Case Management',
  'Medications',
  'Diagnostic Testing',
  'Surgical Procedures',
  'Hospitalizations',
  'Other',
];

export const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY', 'DC',
];

import { z } from 'zod';

export const CaseEventSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  type: z.nativeEnum(CaseEventType),
  date: z.string(),
  time: z.string().optional(),
  description: z.string().optional(),
  location: z.string().optional(),
  attendees: z.array(z.string()).optional(),
  notes: z.string().optional(),
});

export const EVENT_TYPE_LABELS: Record<CaseEventType, string> = {
  [CaseEventType.FILING]: 'Filing',
  [CaseEventType.HEARING]: 'Hearing',
  [CaseEventType.DEPOSITION]: 'Deposition',
  [CaseEventType.MEDIATION]: 'Mediation',
  [CaseEventType.TRIAL]: 'Trial',
  [CaseEventType.MEETING]: 'Meeting',
  [CaseEventType.DEADLINE]: 'Deadline',
  [CaseEventType.OTHER]: 'Other',
};

export const CASE_STATUS_LABELS: Record<CaseStatus, string> = {
  [CaseStatus.INTAKE]: 'Intake',
  [CaseStatus.INVESTIGATION]: 'Investigation',
  [CaseStatus.FILED]: 'Filed',
  [CaseStatus.DISCOVERY]: 'Discovery',
  [CaseStatus.MEDIATION]: 'Mediation',
  [CaseStatus.TRIAL]: 'Trial',
  [CaseStatus.SETTLEMENT]: 'Settlement',
  [CaseStatus.CLOSED]: 'Closed',
};

export enum LifeCarePlanStatus {
  DRAFT = 'draft',
  IN_REVIEW = 'in_review',
  APPROVED = 'approved',
  FINAL = 'final',
}

export const LifeCarePlanSchema = z.object({
  title: z.string().min(1, 'Title is required'),
  status: z.nativeEnum(LifeCarePlanStatus),
  childId: z.string(),
  caseId: z.string(),
  lifeExpectancy: z.number().optional(),
  categories: z.array(z.object({
    id: z.string(),
    name: z.string(),
    items: z.array(z.object({
      description: z.string(),
      annualCost: z.number(),
      frequency: z.string().optional(),
      startAge: z.number().optional(),
      endAge: z.number().optional(),
    })),
  })).optional(),
  notes: z.string().optional(),
});
